import { INSURANCE_TYPES_NAMES } from './insurance-types-names';

export const POLICY_CHECK_INTERVAL = 2000;
export const POLICY_CHECK_MAX_COUNT = 20; // 45 secs max
export const POLICY_CHECK_MAX_LENGTH =
  POLICY_CHECK_INTERVAL * POLICY_CHECK_MAX_COUNT;

export enum PolicyStatus {
  ACTIVE = 'ACTIVE',
  EXPIRED = 'EXPIRED',
  FINISH_SETUP = 'FINISH_SETUP',
  IN_REVIEW = 'IN_REVIEW',
  PROCESSING = 'PROCESSING',
  RENEWING = 'RENEWING',
  INCOMPLETE = 'INCOMPLETE',
  MARKET_SCAN_DRAFT = 'MARKET_SCAN_DRAFT',
  MARKET_SCAN_HOLDER = 'MARKET_SCAN_HOLDER',
  CANCELLED = 'CANCELLED',
}

export const POLICY_STATUS = {
  [PolicyStatus.ACTIVE]: 'Active',
  [PolicyStatus.EXPIRED]: 'Expired',
  [PolicyStatus.FINISH_SETUP]: 'Finish Setup',
  [PolicyStatus.IN_REVIEW]: 'In Review',
  [PolicyStatus.PROCESSING]: 'Processing',
  [PolicyStatus.RENEWING]: 'Renewing',
  [PolicyStatus.INCOMPLETE]: 'Incomplete',
  [PolicyStatus.MARKET_SCAN_DRAFT]: 'Market Scan Draft',
  [PolicyStatus.MARKET_SCAN_HOLDER]: 'Market Scan Holder',
};

export const POLICY_STATUS_TOOLTIPS = {
  [PolicyStatus.ACTIVE]: 'Cover is up-to-date.',
  [PolicyStatus.EXPIRED]:
    "Stay protected, save money, and renew your insurance policy today! It's quick, easy, and offers peace of mind.",
  [PolicyStatus.FINISH_SETUP]:
    'Add your policy to your dashboard. Click to start.',
  [PolicyStatus.IN_REVIEW]: {
    in: 'This shouldn’t take long, we’ll drop you an email when it’s ready in just a few moments.',
    out: 'It’s just past office hours so this might take a little longer than usual. We’ll drop you an email as soon as it’s ready. ',
  },
  [PolicyStatus.INCOMPLETE]: 'Some information is missing. Complete it now.',
  [PolicyStatus.PROCESSING]:
    "We're picking up the information. Stick around, it's almost done.",
  [PolicyStatus.MARKET_SCAN_DRAFT]:
    "You've started a Market Scan, return to Market Scan tab to complete.",
  [PolicyStatus.RENEWING]:
    'Great, your upcoming policy changes will be displayed on the renewal date.',
};

export const POLICY_EMPLOYER_VALUE_MAP = {
  premium: 'Covered',
  frequency: 'With employment',
  policyNumber: 'With employment',
  renewalDate: 'With employment',
  premiumCover: 'With employment',
  paymentMethod: 'With employment',
  excess: 'With employment',
  amount: 'With employment',
  insured: 'With employment',
  policySubTypeId: 'With employment',
  premiumCovers: 'With employment',
};

export const BROKER_POLICY_TYPES = [
  INSURANCE_TYPES_NAMES.VEHICLE,
  INSURANCE_TYPES_NAMES.CONTENT,
  INSURANCE_TYPES_NAMES.HOME,
];

export const POLICY_PRIORITY = {
  [INSURANCE_TYPES_NAMES.VEHICLE]: 15,
  [INSURANCE_TYPES_NAMES.MOTORCYCLE]: 14,
  [INSURANCE_TYPES_NAMES.CONTENT]: 12,
  [INSURANCE_TYPES_NAMES.DISABILITY]: 5,
  [INSURANCE_TYPES_NAMES.HEALTH]: 7,
  [INSURANCE_TYPES_NAMES.HOME_CONTENTS]: 9,
  [INSURANCE_TYPES_NAMES.HOME]: 17,
  [INSURANCE_TYPES_NAMES.INCOME]: 3,
  [INSURANCE_TYPES_NAMES.LIFE]: 8,
  [INSURANCE_TYPES_NAMES.LANDLORD]: 16,
  [INSURANCE_TYPES_NAMES.MORTGAGE]: 2,
  [INSURANCE_TYPES_NAMES.PET]: 11,
  [INSURANCE_TYPES_NAMES.RENTERS]: 10,
  [INSURANCE_TYPES_NAMES.TRAUMA]: 6,
  [INSURANCE_TYPES_NAMES.BOAT]: 13,
  [INSURANCE_TYPES_NAMES.REDUNDANCY]: 1,
  [INSURANCE_TYPES_NAMES.TRAVEL]: 4,
  [INSURANCE_TYPES_NAMES.OTHER]: 0,
};

export enum ProviderNames {
  PROTECTA = 'Assurant',
  PROTECTA_DB_NAME = 'Protecta',
  STAR = 'Star Insure',
  PROVIDENT = 'Provident',
  COVE = 'Cove',
  INITIO = 'Initio',
  AMP = 'AMP',
  AUTOSURE = 'Autosure',
  MAS = 'MAS',
}

export const FullyIntegratedProviders: string[] = [
  ProviderNames.PROVIDENT,
  ProviderNames.PROTECTA_DB_NAME,
  ProviderNames.STAR,
];

export const PartiallyIntegratedProviders: string[] = [
  ProviderNames.COVE,
  ProviderNames.INITIO,
  ProviderNames.AUTOSURE,
];

export const RequestCallbackProviders: string[] = [ProviderNames.MAS];

export const IntegratedProviderDescriptions: Record<
  string,
  { title: string; description: string }
> = {
  [ProviderNames.COVE]: {
    title: 'Trusted & loved by thousands of Kiwis',
    description:
      'Cove enjoys a remarkable 4.8 star rating across hundreds of reviews, which is somewhat unheard of in New Zealand’s insurance industry.',
  },
  [ProviderNames.INITIO]: {
    title: 'Initio is underwritten by IAG, New Zealand’s largest general insurer.',
    description:
      'Easier, faster, smarter house insurance',
  },
  [ProviderNames.AMP]: {
    title: 'Helping Kiwis find the right cover since 1966.',
    description: '',
  },
  [ProviderNames.AUTOSURE]: {
    title: 'Confidence for the Road Ahead',
    description: 'Autosure helps protect your car & your savings',
  },
};

export const PolicyWordingDocs = {
  [INSURANCE_TYPES_NAMES.VEHICLE]: {
    [ProviderNames.PROTECTA_DB_NAME]: [
      {
        fileName: 'Assurant Policy Wording.pdf',
        href: 'https://quashed.co.nz/policy-wordings/protecta/Protecta_Policy_Wording_MVI.pdf',
      },
    ],
    [ProviderNames.PROVIDENT]: [
      {
        fileName: 'Provident Policy Wording.pdf',
        href: 'https://quashed.co.nz/policy-wordings/provident/pi_carminder2.0.pdf',
      },
    ],
    [ProviderNames.AUTOSURE]: [
      {
        policySubType: 'Comprehensive',
        fileName: 'Autosure Comprehensive Policy Wording.pdf',
        href: 'https://quashed.co.nz/policy-wordings/autosure/comprehensive.pdf',
      },
      {
        policySubType: 'Third Party, Fire & Theft',
        fileName: 'Autosure Third Party Fire and Theft Policy Wording.pdf',
        href: 'https://quashed.co.nz/policy-wordings/autosure/tpft.pdf',
      },
      {
        policySubType: 'Third Party',
        fileName: 'Autosure Third Party Policy Wording.pdf',
        href: 'https://quashed.co.nz/policy-wordings/autosure/tppd.pdf',
      },
    ],
  },
  [INSURANCE_TYPES_NAMES.MOTORCYCLE]: {
    [ProviderNames.PROTECTA_DB_NAME]: [
      {
        fileName: 'Assurant Policy Wording.pdf',
        href: 'https://quashed.co.nz/policy-wordings/protecta/Protecta_Policy_Wording_MCI.pdf',
      },
    ],
  },
};
